import { useEffect, useMemo, useState } from 'react';
import type {
  AlertConfigResponse,
  MorningSummaryConfig,
  NotifyChannel,
  Severity,
} from '../../api/alertConfigApi';

// 권위: docs/ARCH_DASHBOARD.md §4.9 섹션 3 — 아침 요약 메일.

const SEVERITIES: Severity[] = ['FATAL', 'ERROR', 'WARN', 'INFO'];
const CHANNELS: NotifyChannel[] = ['email', 'teams', 'webpush'];

function equal(a: MorningSummaryConfig, b: MorningSummaryConfig): boolean {
  if (a.enabled !== b.enabled) return false;
  if (a.sendHhmm !== b.sendHhmm) return false;
  if (a.includeSeverities.length !== b.includeSeverities.length) return false;
  if (a.channels.length !== b.channels.length) return false;
  for (const s of a.includeSeverities) if (!b.includeSeverities.includes(s)) return false;
  for (const c of a.channels) if (!b.channels.includes(c)) return false;
  return true;
}

interface Props {
  config: AlertConfigResponse;
  onSave: (next: MorningSummaryConfig) => void;
  isSaving: boolean;
}

export function MorningSummarySection({
  config,
  onSave,
  isSaving,
}: Props): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<MorningSummaryConfig>(config.morningSummary);

  useEffect(() => {
    if (!editing) setDraft(config.morningSummary);
  }, [config.morningSummary, editing]);

  const dirty = useMemo(
    () => !equal(draft, config.morningSummary),
    [draft, config.morningSummary]
  );

  const toggleSeverity = (s: Severity): void => {
    setDraft((prev) => ({
      ...prev,
      includeSeverities: prev.includeSeverities.includes(s)
        ? prev.includeSeverities.filter((x) => x !== s)
        : [...prev.includeSeverities, s],
    }));
  };
  const toggleChannel = (c: NotifyChannel): void => {
    setDraft((prev) => ({
      ...prev,
      channels: prev.channels.includes(c)
        ? prev.channels.filter((x) => x !== c)
        : [...prev.channels, c],
    }));
  };

  const startEdit = (): void => {
    setDraft(config.morningSummary);
    setEditing(true);
  };
  const cancel = (): void => {
    setDraft(config.morningSummary);
    setEditing(false);
  };
  const save = (): void => {
    onSave(draft);
    setEditing(false);
  };

  return (
    <section className="as-card" data-testid="as-summary-section">
      <div className="as-section__head">
        <div>
          <h3 className="as-section__title">3. 아침 요약</h3>
          <div className="as-section__sub">
            매일 지정 시각에 선정된 심각도 이벤트의 다이제스트를 지정 채널로 발송합니다.
          </div>
        </div>
        <div className="as-section__actions">
          {dirty && <span className="as-dirty">저장되지 않음</span>}
          {!editing ? (
            <button type="button" className="as-btn" onClick={startEdit}>
              편집
            </button>
          ) : (
            <>
              <button
                type="button"
                className="as-btn as-btn--ghost"
                onClick={cancel}
                disabled={isSaving}
              >
                취소
              </button>
              <button
                type="button"
                className="as-btn as-btn--primary"
                onClick={save}
                disabled={!dirty || isSaving}
              >
                저장
              </button>
            </>
          )}
        </div>
      </div>

      <div className="as-row">
        <label className="as-chip">
          <input
            type="checkbox"
            checked={draft.enabled}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({ ...p, enabled: e.target.checked }))
            }
          />
          <span>활성화</span>
        </label>

        <div className="as-field">
          <label className="as-field__label" htmlFor="as-summary-sendhhmm">
            발송 시각 (HH:MM)
          </label>
          <input
            id="as-summary-sendhhmm"
            type="time"
            className="as-input"
            value={draft.sendHhmm}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({ ...p, sendHhmm: e.target.value }))
            }
          />
        </div>
      </div>

      <div>
        <div className="as-field__label" style={{ marginBottom: 4 }}>
          포함할 severity
        </div>
        <div className="as-chip-row">
          {SEVERITIES.map((s) => {
            const active = draft.includeSeverities.includes(s);
            return (
              <label
                key={s}
                className={`as-chip ${active ? 'as-chip--active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  disabled={!editing || isSaving}
                  onChange={() => toggleSeverity(s)}
                />
                <span>{s}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <div className="as-field__label" style={{ marginBottom: 4 }}>
          발송 채널
        </div>
        <div className="as-chip-row">
          {CHANNELS.map((c) => {
            const active = draft.channels.includes(c);
            return (
              <label
                key={c}
                className={`as-chip ${active ? 'as-chip--active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  disabled={!editing || isSaving}
                  onChange={() => toggleChannel(c)}
                />
                <span>{c}</span>
              </label>
            );
          })}
        </div>
      </div>
    </section>
  );
}
